package terraultima.web

import terraultima.game.*

/**
 * The whole state of a century, as far as the browser is concerned.
 *
 * `Frontend.present()` returns the number the player picked — it BLOCKS, and JavaScript has one
 * thread and cannot. The alternative would be making the engine suspend, which colours
 * `Engine.run`, both terminal frontends and the harness, and forks the one simulation loop that
 * every number in `docs/PLAYTEST-LOG.md` describes.
 *
 * So the browser does what `./play script` and the cold-read instrument already do: it holds no live
 * session at all. Seed plus the list of numbers tapped reproduces the run exactly, so every tap
 * replays the run from year zero, answers from the list, and throws [Stop] the moment it reaches a
 * question the list cannot answer. Measured on a JVM, a full hundred years is ~130 ms; in a browser
 * it is a few hundred at the very end of a long run, and near-instant for most of one.
 *
 * The payoff is that this page needs no server, no session, and no coordination: the save is
 * `{seed, motive, choices}` and anything that can store 300 bytes can host a century.
 */
class Stop : RuntimeException("enough")

/** Everything the interface draws, frozen at the moment the engine stopped to ask. */
class Snapshot(
    val colony: ColonyView,
    val modules: List<ModuleView>,
    val budgets: List<BudgetView>,
    val crewNote: List<String>,
    val history: List<HistoryEntry>,
    val facts: List<PlanetFactView>,
    val planetName: String,
    val goals: List<GoalView>,
    val potential: List<String>
) {
    companion object {
        fun of(s: SessionInfo) = Snapshot(
            colony = s.colony(), modules = s.modules(), budgets = s.budgets(),
            crewNote = s.crewNote(), history = s.history(), facts = s.planetFacts(),
            planetName = s.planetName(), goals = s.goals(), potential = s.planetPotential()
        )
    }
}

class RunResult(
    /** Non-null when the expedition is assembled and the player has not launched yet. */
    val setup: SetupView?,
    /** The question the run is waiting on. */
    val decision: DecisionView?,
    val ending: EndingView?,
    val snap: Snapshot?,
    /** Year reports since the player was last asked. */
    val digest: List<String>,
    /** Gambles that resolved since the player was last asked. */
    val outcomes: List<String>,
    val answered: Int,
    /**
     * How many replayed taps could not be honoured.
     *
     * `GameSession.choose` falls back to the first offered option when the stored number is not
     * available, which is the right behaviour — refusing to resume would throw away someone's
     * century over a content edit. But it is silent, and a silent substitution means the run on
     * screen is not the run in the save: a shared link shows the recipient a different century than
     * the sender played, and a corpus edit quietly rewrites history. Principle A3 says a claim the
     * player cannot check reads as arbitrary, so this is counted and said out loud.
     */
    val diverged: Int
)

object Run {

    fun replay(seed: Int, motive: Int, choices: List<Int>, launched: Boolean): RunResult {
        val fe = Collector(motive, choices, launched)
        try {
            GameSession(fe, seed = seed).play()
        } catch (e: Stop) {
            // Not an error: it is how a run pauses when it has nothing left to replay.
        }
        return RunResult(fe.setup, fe.pending, fe.ending, fe.snap,
            fe.digest.toList(), fe.outcomes.toList(), fe.answered, fe.diverged)
    }

    private class Collector(
        private val motive: Int,
        private val answers: List<Int>,
        private val launched: Boolean
    ) : Frontend {

        private lateinit var info: SessionInfo
        var setup: SetupView? = null
        var pending: DecisionView? = null
        var ending: EndingView? = null
        var snap: Snapshot? = null
        var answered = 0
        var diverged = 0
        val digest = mutableListOf<String>()
        val outcomes = mutableListOf<String>()

        override fun attach(session: SessionInfo) { info = session }

        override fun chooseMotive(motives: List<MotiveView>, default: Int): Int = motive

        override fun reviewSetup(setup: SetupView) {
            this.setup = setup
            // The launch tap is a real decision — it is the last moment the expedition can be read
            // before a century starts happening to it — but the engine has no gate for it, because
            // in a terminal the frontend simply blocks. Here it is one saved boolean.
            if (!launched) { snap = Snapshot.of(info); throw Stop() }
        }

        override fun present(decision: DecisionView): Int {
            if (answered < answers.size) {
                val n = answers[answered++]
                // The engine will substitute the first offered option for an unavailable one and say
                // nothing. Notice, so the interface can.
                if (decision.options.firstOrNull { it.number == n }?.available != true) diverged++
                // Everything that happened while replaying is already in the past by definition.
                digest.clear()
                outcomes.clear()
                return n
            }
            pending = decision
            snap = Snapshot.of(info)
            throw Stop()
        }

        override fun outcome(text: String) { outcomes += text }

        override fun yearReport(report: YearReportView) {
            // Same rule as every other frontend: a year number on its own is not a report.
            if (report.notes.isEmpty() && report.faultsAbsorbed == 0) return
            digest += "year ${report.year}" +
                    (if (report.decisionsThisYear == 0) " — nothing demanded your attention" else "")
            digest += report.notes
            if (report.faultsAbsorbed > 0)
                digest += "${report.faultsAbsorbed} fault(s) were repaired without troubling you"
        }

        override fun ending(e: EndingView) {
            ending = e
            snap = Snapshot.of(info)
        }
    }
}
